import React, { useState } from 'react';
import { X, Save, Key, Filter, Clock, Database, Book, AlertCircle } from 'lucide-react';
import APISetupGuide from './APISetupGuide';

const ConfigurationPanel = ({ platform, onClose, onSave }) => {
  const [config, setConfig] = useState({
    apiKey: '',
    apiSecret: '',
    dataTypes: platform?.dataTypes || [],
    keywords: [],
    hashtags: [],
    geoRegions: [],
    collectionFrequency: 5,
    retentionDays: 90,
    rateLimitPerHour: 1000
  });

  const [newKeyword, setNewKeyword] = useState('');
  const [newHashtag, setNewHashtag] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleDataTypeToggle = (type) => {
    setConfig(prev => ({
      ...prev,
      dataTypes: prev?.dataTypes?.includes(type)
        ? prev?.dataTypes?.filter(t => t !== type)
        : [...prev?.dataTypes, type]
    }));
  };

  const addKeyword = () => {
    if (newKeyword?.trim()) {
      setConfig(prev => ({
        ...prev,
        keywords: [...prev?.keywords, newKeyword?.trim()]
      }));
      setNewKeyword('');
    }
  };

  const addHashtag = () => {
    if (newHashtag?.trim()) {
      setConfig(prev => ({
        ...prev,
        hashtags: [...prev?.hashtags, newHashtag?.trim()]
      }));
      setNewHashtag('');
    }
  };

  const addRegion = () => {
    if (newRegion?.trim()) {
      setConfig(prev => ({
        ...prev,
        geoRegions: [...prev?.geoRegions, newRegion?.trim()]
      }));
      setNewRegion('');
    }
  };

  const removeItem = (key, item) => {
    setConfig(prev => ({
      ...prev,
      [key]: prev?.[key]?.filter(i => i !== item)
    }));
  };

  const getCredentialFields = () => {
    const fieldConfigs = {
      twitter: [
        { name: 'apiKey', label: 'API Key', type: 'text', required: true },
        { name: 'apiSecret', label: 'API Secret', type: 'password', required: true },
        { name: 'accessToken', label: 'Access Token', type: 'text', required: true },
        { name: 'accessTokenSecret', label: 'Access Token Secret', type: 'password', required: true },
        { name: 'bearerToken', label: 'Bearer Token', type: 'password', required: true }
      ],
      facebook: [
        { name: 'accessToken', label: 'Page Access Token', type: 'password', required: true },
        { name: 'pageId', label: 'Page ID', type: 'text', required: true }
      ],
      instagram: [
        { name: 'accessToken', label: 'Access Token', type: 'password', required: true },
        { name: 'accountId', label: 'Instagram Business Account ID', type: 'text', required: true }
      ],
      linkedin: [
        { name: 'accessToken', label: 'Access Token', type: 'password', required: true },
        { name: 'organizationId', label: 'Organization ID', type: 'text', required: true }
      ],
      reddit: [
        { name: 'clientId', label: 'Client ID', type: 'text', required: true },
        { name: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
        { name: 'username', label: 'Reddit Username', type: 'text', required: true },
        { name: 'password', label: 'Reddit Password', type: 'password', required: true }
      ],
      youtube: [
        { name: 'apiKey', label: 'YouTube API Key', type: 'password', required: true }
      ],
      tiktok: [
        { name: 'accessToken', label: 'Access Token', type: 'password', required: true }
      ],
      google_reviews: [
        { name: 'apiKey', label: 'Google Places API Key', type: 'password', required: true },
        { name: 'placeId', label: 'Place ID', type: 'text', required: true }
      ],
      yelp: [
        { name: 'apiKey', label: 'Yelp API Key', type: 'password', required: true },
        { name: 'businessId', label: 'Business ID', type: 'text', required: true }
      ],
      trustpilot: [
        { name: 'apiKey', label: 'Trustpilot API Key', type: 'password', required: true },
        { name: 'businessUnitId', label: 'Business Unit ID', type: 'text', required: true }
      ],
      amazon_reviews: [
        { name: 'accessKey', label: 'Amazon Access Key', type: 'text', required: true },
        { name: 'secretKey', label: 'Amazon Secret Key', type: 'password', required: true },
        { name: 'productId', label: 'Product ID', type: 'text', required: true }
      ],
      app_store: [
        { name: 'appId', label: 'App Store App ID', type: 'text', required: true }
      ],
      google_play: [
        { name: 'packageName', label: 'Package Name', type: 'text', required: true }
      ],
      news_api: [
        { name: 'apiKey', label: 'News API Key', type: 'password', required: true }
      ]
    };

    return fieldConfigs?.[platform] || [];
  };

  const handleCredentialChange = (fieldName, value) => {
    setCredentials(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSaveCredentials = async () => {
    setIsLoading(true);
    try {
      // Validate required fields
      const fields = getCredentialFields();
      const missingFields = fields
        ?.filter(field => field?.required && !credentials?.[field?.name])
        ?.map(field => field?.label);

      if (missingFields?.length > 0) {
        alert(`Please fill in required fields: ${missingFields?.join(', ')}`);
        return;
      }

      // Add this: Mock platform config service or handle credentials locally
      // Since platformConfigService is not defined, we'll simulate the save
      console.log('Saving credentials for platform:', platform, credentials);
      
      // If you have an actual service, import it at the top:
      // import { platformConfigService } from '../../../services/platformConfigService';
      // Then uncomment the line below:
      // await platformConfigService?.updateConfiguration(platform, {
      //   apiCredentials: credentials,
      //   collectionEnabled: true
      // });

      alert('API credentials saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving credentials:', error);
      alert('Failed to save credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{platform?.icon}</span>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {platform?.name} Configuration
              </h2>
              <p className="text-sm text-gray-600">
                Configure data collection and authentication settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Setup Guide Toggle */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Book className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Need help setting up?</p>
                  <p className="text-sm text-blue-700">View step-by-step integration guide</p>
                </div>
              </div>
              <button
                onClick={() => setShowGuide(!showGuide)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                {showGuide ? 'Hide Guide' : 'Show Guide'}
              </button>
            </div>
          </div>

          {/* API Setup Guide */}
          {showGuide ? (
            <APISetupGuide 
              platform={platform}
              onComplete={() => setShowGuide(false)}
            />
          ) : (
            <>
              {/* API Credentials Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-gray-700" />
                  <h3 className="text-lg font-semibold text-gray-900">API Credentials</h3>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Important Security Notice</p>
                      <p>Your API credentials are encrypted and stored securely. Never share your credentials with unauthorized parties.</p>
                    </div>
                  </div>
                </div>

                {/* Credential Input Fields */}
                <div className="space-y-4">
                  {getCredentialFields()?.map((field) => (
                    <div key={field?.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field?.label}
                        {field?.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <input
                        type={field?.type}
                        value={credentials?.[field?.name] || ''}
                        onChange={(e) => handleCredentialChange(field?.name, e?.target?.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={`Enter your ${field?.label?.toLowerCase()}`}
                        required={field?.required}
                      />
                    </div>
                  ))}
                </div>

                {/* Save Credentials Button */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSaveCredentials}
                    disabled={isLoading}
                    className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Credentials
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowGuide(true)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    View Guide
                  </button>
                </div>
              </div>

              {/* Data Collection */}
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Database className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Data Collection Types</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {['posts', 'comments', 'reviews', 'mentions', 'reactions', 'shares']?.map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config?.dataTypes?.includes(type)}
                        onChange={() => handleDataTypeToggle(type)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </section>

              {/* Filters */}
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Filters</h3>
                </div>
                
                {/* Keywords */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keywords
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && addKeyword()}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add keyword"
                    />
                    <button
                      onClick={addKeyword}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {config?.keywords?.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-2"
                      >
                        {keyword}
                        <button
                          onClick={() => removeItem('keywords', keyword)}
                          className="hover:text-blue-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Hashtags */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hashtags
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newHashtag}
                      onChange={(e) => setNewHashtag(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && addHashtag()}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add hashtag (without #)"
                    />
                    <button
                      onClick={addHashtag}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {config?.hashtags?.map((hashtag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm flex items-center gap-2"
                      >
                        #{hashtag}
                        <button
                          onClick={() => removeItem('hashtags', hashtag)}
                          className="hover:text-purple-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Geographic Regions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Geographic Regions
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newRegion}
                      onChange={(e) => setNewRegion(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && addRegion()}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add region (e.g., United States, Europe)"
                    />
                    <button
                      onClick={addRegion}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {config?.geoRegions?.map((region, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm flex items-center gap-2"
                      >
                        {region}
                        <button
                          onClick={() => removeItem('geoRegions', region)}
                          className="hover:text-green-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </section>

              {/* Advanced Settings */}
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Advanced Settings</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Collection Frequency (minutes)
                    </label>
                    <input
                      type="number"
                      value={config?.collectionFrequency}
                      onChange={(e) => setConfig(prev => ({ ...prev, collectionFrequency: parseInt(e?.target?.value) }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Retention (days)
                    </label>
                    <input
                      type="number"
                      value={config?.retentionDays}
                      onChange={(e) => setConfig(prev => ({ ...prev, retentionDays: parseInt(e?.target?.value) }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rate Limit (requests/hour)
                    </label>
                    <input
                      type="number"
                      value={config?.rateLimitPerHour}
                      onChange={(e) => setConfig(prev => ({ ...prev, rateLimitPerHour: parseInt(e?.target?.value) }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="1"
                    />
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(config)}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPanel;